import { useState } from "react";
import { Plane, TrainFront, Clock, ArrowRight, Pencil } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { CalendarButton } from "./CalendarButton";
import { useTrip } from "@/lib/store";
import { fmtTime, fmtShort, parseTimeInput } from "@/lib/dates";

const F = ({ label, value, onChange, testId, placeholder }) => (
  <label className="block text-xs"><span className="eyebrow">{label}</span>
    <input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full rounded-xl border bg-white px-3 py-2 text-sm" /></label>
);

export function TransportCard({ t, compact = false }) {
  const { organiser, updateTransport } = useTrip();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ carrier: t.carrier, flightNumber: t.flightNumber, fromPlace: t.fromPlace, toPlace: t.toPlace, departTime: t.departTime ? fmtTime(t.departTime) : "", arriveTime: t.arriveTime ? fmtTime(t.arriveTime) : "", notes: t.notes });
  const isFlight = t.type === "flight";
  const Icon = isFlight ? Plane : TrainFront;

  const save = () => {
    const dep = form.departTime ? parseTimeInput(form.departTime) : null;
    const arr = form.arriveTime ? parseTimeInput(form.arriveTime) : null;
    const missing = [];
    if (isFlight && !form.carrier) missing.push("Airline");
    if (isFlight && !form.flightNumber) missing.push("Flight number");
    if (!form.fromPlace) missing.push(isFlight ? "Departure airport" : "Departure station");
    if (!isFlight && !form.toPlace) missing.push("Arrival station");
    if (!dep) missing.push("Departure time");
    updateTransport(t.id, { carrier: form.carrier || null, flightNumber: form.flightNumber || null, fromPlace: form.fromPlace || null, toPlace: form.toPlace || null, departTime: dep, arriveTime: arr, notes: form.notes, missing, status: missing.length ? (dep ? "planned" : "tbc") : "confirmed" });
    setEdit(false);
  };

  return (
    <article data-testid={`transport-card-${t.id}`} className={`relative overflow-hidden rounded-3xl border ${isFlight ? "bg-[var(--jade-deep)] text-[var(--parchment)]" : "bg-white"}`}>
      {isFlight && <div className="grain absolute inset-0" />}
      <div className="relative p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${isFlight ? "bg-white/10" : "bg-[var(--jade-soft)] text-[var(--jade)]"}`}><Icon className="h-5 w-5" /></span>
            <div>
              <p className={`eyebrow ${isFlight ? "!text-[var(--parchment)]/70" : ""}`}>{isFlight ? "Flight" : "Train"} · {fmtShort(t.date)}</p>
              <h3 className="font-display text-2xl font-semibold leading-tight">{t.title}</h3>
            </div>
          </div>
          <StatusBadge status={t.status} />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1">
            <p className={`text-xs ${isFlight ? "text-[var(--parchment)]/70" : "text-[var(--ink-muted)]"}`}>{t.fromPlace || t.fromCity}</p>
            <p data-testid={`transport-depart-${t.id}`} className="font-display text-3xl font-semibold">{t.departTime ? fmtTime(t.departTime) : "TBC"}</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <ArrowRight className={`h-5 w-5 ${isFlight ? "text-[var(--brass)]" : "text-[var(--jade)]"}`} />
            {t.duration && <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${isFlight ? "text-[var(--parchment)]/80" : "text-[var(--ink-muted)]"}`}><Clock className="h-3 w-3" /> {t.duration}</span>}
          </div>
          <div className="flex-1 text-right">
            <p className={`text-xs ${isFlight ? "text-[var(--parchment)]/70" : "text-[var(--ink-muted)]"}`}>{t.toPlace || t.toCity}</p>
            <p data-testid={`transport-arrive-${t.id}`} className="font-display text-3xl font-semibold">{t.arriveTime ? fmtTime(t.arriveTime) : "TBC"}</p>
          </div>
        </div>
        <div className={`mt-3 flex flex-wrap gap-x-3 gap-y-1 text-sm ${isFlight ? "text-[var(--parchment)]/85" : "text-[var(--ink-muted)]"}`}>
          {t.carrier && <span className="font-semibold">{t.carrier}</span>}
          {t.flightNumber && <span className="font-mono">{t.flightNumber}</span>}
          {t.notes && <span>{t.notes}</span>}
        </div>
        {t.missing?.length > 0 && !compact && (
          <p data-testid={`transport-missing-${t.id}`} className={`mt-3 rounded-xl px-3 py-2 text-xs leading-relaxed ${isFlight ? "bg-white/10" : "bg-indigo-50 text-indigo-900"}`}>
            <span className="font-semibold">TBC:</span> {t.missing.join(" · ")}
          </p>
        )}
        {!compact && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <CalendarButton kind="transport" item={t} label={t.departTime ? "Add to calendar" : "Add day to calendar"} testId={`calendar-transport-btn-${t.id}`} />
            {organiser && <button data-testid={`complete-details-btn-${t.id}`} onClick={() => setEdit((e) => !e)} className={`tap inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold ${isFlight ? "bg-[var(--brass)] text-[var(--jade-deep)]" : "bg-[var(--plum)] text-white"}`}><Pencil className="h-4 w-4" /> {edit ? "Close" : "Complete details"}</button>}
          </div>
        )}
        {edit && (
          <div data-testid={`transport-edit-form-${t.id}`} className="mt-4 grid grid-cols-2 gap-3 rounded-2xl bg-white p-3 text-[var(--ink)]">
            <F label={isFlight ? "Airline" : "Operator"} value={form.carrier} onChange={(v) => setForm({ ...form, carrier: v })} testId={`edit-carrier-${t.id}`} />
            {isFlight && <F label="Flight number" value={form.flightNumber} onChange={(v) => setForm({ ...form, flightNumber: v })} testId={`edit-flightno-${t.id}`} />}
            <F label={isFlight ? "Departure airport" : "Departure station"} value={form.fromPlace} onChange={(v) => setForm({ ...form, fromPlace: v })} testId={`edit-from-${t.id}`} />
            <F label={isFlight ? "Arrival airport" : "Arrival station"} value={form.toPlace} onChange={(v) => setForm({ ...form, toPlace: v })} testId={`edit-to-${t.id}`} />
            <F label="Departs" value={form.departTime} onChange={(v) => setForm({ ...form, departTime: v })} testId={`edit-depart-${t.id}`} placeholder="e.g. 9:15 AM" />
            <F label="Arrives" value={form.arriveTime} onChange={(v) => setForm({ ...form, arriveTime: v })} testId={`edit-arrive-${t.id}`} placeholder="e.g. 11:55 AM" />
            <label className="col-span-2 block text-xs"><span className="eyebrow">Notes</span><input data-testid={`edit-notes-${t.id}`} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-xl border bg-white px-3 py-2 text-sm" /></label>
            <button data-testid={`save-transport-${t.id}`} onClick={save} className="tap col-span-2 rounded-full bg-[var(--jade)] py-2.5 text-sm font-semibold text-[var(--parchment)]">Save details</button>
          </div>
        )}
      </div>
    </article>
  );
}
